import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { ColDef } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { ColumnMeta } from '../../../types';
import { SimpleColumnProvider, useSimpleColumnProvider } from '../../../components/ColumnManagement';
import { useColumns } from '../hooks/useColumns/useColumns';
import { pfGridApiAtom } from '../components/OverviewTable/PfState';
import { columnSort } from './column-utils';

interface ColumnSelectorContextInterface {
  activeOrder: string[];
  setActiveOrder: (order: string[]) => void;
  sortedColumns: ColumnMeta[];
}

// The column universe the selector operates on, in source order. Kept in
// context so the dropdown stack works for any grid, not just the company one.
const ColumnSelectorColumnsContext = createContext<ColumnMeta[]>([]);

// Company grid: the universe is the view's column metadata.
export const ColumnSelectorProvider: FC<PropsWithChildren> = ({ children }) => {
  const allColumns = useColumns();
  return <ColumnSelectorProviderBase columns={allColumns}>{children}</ColumnSelectorProviderBase>;
};

// Grid-agnostic core: the caller supplies the column universe (e.g. the
// security grid derives it from its colDefs).
export const ColumnSelectorProviderBase: FC<PropsWithChildren<{ columns: ColumnMeta[] }>> = ({
  columns,
  children,
}) => {
  const gridApi = useAtomValue(pfGridApiAtom);

  const [activeOrder, setActiveOrder] = useState<string[]>([]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const order =
      gridApi
        .getColumnState()
        ?.filter((colDef) => (colDef as ColDef)?.colId && !(colDef as ColDef)?.hide)
        ?.map((colDef) => (colDef as ColDef).colId as string) ?? [];

    setActiveOrder(order);
  }, []); // eslint-disable-line

  return (
    <ColumnSelectorColumnsContext.Provider value={columns}>
      <SimpleColumnProvider initialColumns={activeOrder}>
        <ColumnSelectorContextWrapper
          activeOrder={activeOrder}
          setActiveOrder={setActiveOrder}
          sortedColumns={columns}
        >
          {children}
        </ColumnSelectorContextWrapper>
      </SimpleColumnProvider>
    </ColumnSelectorColumnsContext.Provider>
  );
};

const ColumnSelectorContextWrapper: FC<{
  children: React.ReactNode;
  activeOrder: string[];
  setActiveOrder: (order: string[]) => void;
  sortedColumns: ColumnMeta[];
}> = ({ children }) => {
  return <>{children}</>;
};

// Raw (source-order) universe, for consumers that build lookups or option
// lists and must not re-sort (SelectedColumns, ColumnItemDropdown).
export const useSelectorColumns = (): ColumnMeta[] => {
  return useContext(ColumnSelectorColumnsContext);
};

export const useColumnSelector = (): ColumnSelectorContextInterface => {
  const { selectedColumns, setSelectedColumns } = useSimpleColumnProvider();

  const allColumns = useSelectorColumns();
  const sortedColumns = useMemo(() => {
    return [...allColumns].sort(columnSort);
  }, [allColumns]);

  return {
    activeOrder: selectedColumns,
    setActiveOrder: setSelectedColumns,
    sortedColumns,
  };
};
